import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import useAppTheme from 'hooks/useAppTheme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import DispatchMonitorAPI, {
  type LatestBlendEntry,
  type LatestBlendDumpingPoint,
  type HourlyBlendResponse,
} from 'api/dispatch-monitor.api';
import FleetPlanningAPI from 'api/fleet-planning.api';
import LocationImporterAPI from 'api/location-importer.api';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { useAppSelector } from 'store/hook';
import EventSourcePolyfillModule from 'event-source-polyfill';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

type EventSourcePolyfillType = new (
  url: string,
  init?: { headers?: Record<string, string> }
) => EventSource;

const EventSourcePolyfill = (EventSourcePolyfillModule as any)
  .EventSourcePolyfill as EventSourcePolyfillType;

interface LoadingPointRow {
  loadingPoint: string;
  materialGrade: string;
  trips: number;
  tonnage: number;
  blendValues: Record<string, number>;
}

interface DumpingPointGroup {
  name: string;
  targetGrade: string;
  totalTonnage: number;
  avgBlends: Record<string, number>;
  loadingPoints: LoadingPointRow[];
}

interface HourlyLpData {
  trips: number;
  tonnage: number;
  blendValues: Record<string, number>;
}

interface HourlyDpSlot {
  summaryTonnage: number;
  avgBlends: Record<string, number>;
  lpData: Record<string, HourlyLpData>;
}

interface HourColumn {
  hour: string;
  label: string;
}

function getTargetGrade(
  dpName: string,
  blendMap: Record<string, number>
): string {
  const key = Object.keys(blendMap).find(
    (k) => k.toLowerCase() === dpName.toLowerCase()
  );
  return key != null ? `${blendMap[key]}%` : '—';
}

function flattenQualities(
  qualities: Array<Record<string, number>> | undefined
): Record<string, number> {
  const sums: Record<string, { sum: number; count: number }> = {};
  (qualities ?? []).forEach((quality) => {
    Object.entries(quality ?? {}).forEach(([mineral, val]) => {
      if (typeof val !== 'number') return;
      if (!sums[mineral]) sums[mineral] = { sum: 0, count: 0 };
      sums[mineral].sum += val;
      sums[mineral].count += 1;
    });
  });
  const result: Record<string, number> = {};
  Object.entries(sums).forEach(([mineral, { sum, count }]) => {
    result[mineral] = sum / count;
  });
  return result;
}

function buildGroups(
  dumpingPoints: LatestBlendDumpingPoint[],
  blendMap: Record<string, number>
): DumpingPointGroup[] {
  return dumpingPoints.map((dp) => {
    const loadingPoints: LoadingPointRow[] = Object.entries(dp.data).map(
      ([lp, entry]) => {
        const blend = flattenQualities(entry.MaterialBlend?.qualities);
        const materialGrade =
          entry.material !== ''
            ? entry.material
            : Object.keys(blend).length > 0
            ? Object.entries(blend)
                .map(([k, v]) => `${k}: ${v}`)
                .join(', ')
            : lp;
        return {
          loadingPoint: lp,
          materialGrade,
          trips: entry.trips ?? 0,
          tonnage: entry.totalMaterialDumped ?? 0,
          blendValues: blend,
        };
      }
    );

    const totalTonnage = loadingPoints.reduce((s, r) => s + r.tonnage, 0);

    const mineralSums: Record<string, { sum: number; count: number }> = {};
    loadingPoints.forEach((row) => {
      Object.entries(row.blendValues).forEach(([mineral, val]) => {
        if (!mineralSums[mineral]) mineralSums[mineral] = { sum: 0, count: 0 };
        mineralSums[mineral].sum += val;
        mineralSums[mineral].count += 1;
      });
    });
    const avgBlends: Record<string, number> = {};
    Object.entries(mineralSums).forEach(([mineral, { sum, count }]) => {
      avgBlends[mineral] = sum / count;
    });

    return {
      name: dp.dumpingPointName.toUpperCase(),
      targetGrade: getTargetGrade(dp.dumpingPointName, blendMap),
      totalTonnage,
      avgBlends,
      loadingPoints,
    };
  });
}

function processHourlyResponse(
  res: HourlyBlendResponse,
  customerTimezone: string
): {
  hourColumns: HourColumn[];
  hourlyMatrix: Record<string, Record<string, HourlyDpSlot>>;
} {
  // Parse API date "DD-MM-YYYY" → "YYYY-MM-DD" for dayjs
  const [dd, mm, yyyy] = res.date.split('-');
  const isoDate = `${yyyy}-${mm}-${dd}`;

  const hoursWithData = new Set<string>();
  const matrix: Record<string, Record<string, HourlyDpSlot>> = {};

  res.dumpingPoints.forEach((dp) => {
    const dpKey = dp.dumpingPointName.toLowerCase();
    matrix[dpKey] = {};

    dp.hourly.forEach(({ hour, data }) => {
      if (!data) return;
      hoursWithData.add(hour);

      const lpData: Record<string, HourlyLpData> = {};
      Object.entries(data).forEach(([lpKey, entry]) => {
        const key = entry.loadingPoint ?? lpKey;
        lpData[key] = {
          trips: entry.trips ?? 0,
          tonnage: entry.totalMaterialDumped ?? 0,
          blendValues: flattenQualities(entry.MaterialBlend?.qualities),
        };
      });

      const summaryTonnage = Object.values(lpData).reduce(
        (s, r) => s + r.tonnage,
        0
      );
      const mineralSums: Record<string, { sum: number; count: number }> = {};
      Object.values(lpData).forEach((lp) => {
        Object.entries(lp.blendValues).forEach(([m, v]) => {
          if (!mineralSums[m]) mineralSums[m] = { sum: 0, count: 0 };
          mineralSums[m].sum += v;
          mineralSums[m].count += 1;
        });
      });
      const avgBlends: Record<string, number> = {};
      Object.entries(mineralSums).forEach(([m, { sum, count }]) => {
        avgBlends[m] = sum / count;
      });

      matrix[dpKey][hour] = { summaryTonnage, avgBlends, lpData };
    });
  });

  const sortedHours = Array.from(hoursWithData).sort();
  const hourColumns: HourColumn[] = sortedHours.map((h) => ({
    hour: h,
    label: dayjs
      .utc(`${isoDate}T${h.padStart(2, '0')}:00:00Z`)
      .tz(customerTimezone)
      .format('h:00 A'),
  }));

  return { hourColumns, hourlyMatrix: matrix };
}

export const BlendMonitoring = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const customerCode = useAppSelector(
    (state) => state.persistedReducer.customer.code
  );
  const customerTimezone = useAppSelector(
    (state) => state.persistedReducer.customer.timezone
  );
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);

  const dispatchApi = useRef(new DispatchMonitorAPI()).current;
  const fleetApi = useRef(new FleetPlanningAPI()).current;
  const locationApi = useRef(new LocationImporterAPI()).current;

  const [groups, setGroups] = useState<DumpingPointGroup[]>([]);
  const [forecastTonnage, setForecastTonnage] = useState<string>('—');
  const [forecastGrade] = useState<string>('—');
  const [loading, setLoading] = useState(true);
  const [hourColumns, setHourColumns] = useState<HourColumn[]>([]);
  const [hourlyMatrix, setHourlyMatrix] = useState<
    Record<string, Record<string, HourlyDpSlot>>
  >({});
  const blendMapRef = useRef<Record<string, number>>({});
  const flatDataRef = useRef<Record<string, LatestBlendEntry>>({});
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to show the last 2 hour columns on load / SSE update
  useEffect(() => {
    if (tableContainerRef.current && hourColumns.length > 0) {
      tableContainerRef.current.scrollLeft = Math.max(
        0,
        (hourColumns.length - 2) * 130
      );
    }
  }, [hourColumns]);

  const fetchHourlyBlend = useCallback(async () => {
    if (!customerId) return;
    try {
      const date = dayjs.utc().format('DD-MM-YYYY');
      const res = await dispatchApi.getHourlyBlend(customerId, date);
      const tz = customerTimezone ?? 'UTC';
      const { hourColumns: cols, hourlyMatrix: matrix } = processHourlyResponse(
        res,
        tz
      );
      setHourColumns(cols);
      setHourlyMatrix(matrix);
    } catch (_) {}
  }, [customerId, customerTimezone]);

  useEffect(() => {
    if (!customerId) return;
    Promise.all([
      dispatchApi.getLatestBlend(),
      fleetApi.getLatestApprovedDispatchPlan(customerId),
      locationApi.getLocations(),
      (async () => {
        const date = dayjs.utc().format('DD-MM-YYYY');
        return await dispatchApi.getHourlyBlend(customerId, date);
      })(),
    ])
      .then(([blendRes, planRes, locations, hourlyRes]) => {
        const rawMap: Record<string, number> = {};
        (locations ?? []).forEach((loc) => {
          const targetBlend = loc.meta?.targetBlend;
          if (typeof targetBlend === 'number' && loc.name) {
            rawMap[loc.name] = targetBlend;
          }
        });
        blendMapRef.current = rawMap;

        const flatMap: Record<string, LatestBlendEntry> = {};
        blendRes.dumpingPoints.forEach((dp) => {
          Object.entries(dp.data).forEach(([lpKey, entry]) => {
            flatMap[lpKey] = {
              ...entry,
              dumpingZone: entry.dumpingZone || dp.dumpingPointName,
            };
          });
        });
        flatDataRef.current = flatMap;

        setGroups(buildGroups(blendRes.dumpingPoints, rawMap));

        const prodTarget =
          planRes?.inputData?.productionTarget ??
          planRes?.inputData?.production_target;
        setForecastTonnage(prodTarget != null ? String(prodTarget) : '—');

        const tz = customerTimezone ?? 'UTC';
        const { hourColumns: cols, hourlyMatrix: matrix } =
          processHourlyResponse(hourlyRes, tz);
        setHourColumns(cols);
        setHourlyMatrix(matrix);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }, [customerId]);

  // SSE subscription for live blend updates
  useEffect(() => {
    if (!accessToken || !customerId || !customerCode) return;
    const base = process.env.REACT_APP_MANAGEMENT_API_URL ?? '';
    const channel = `xtab:${customerCode}:blend:pubsub`;
    const es = new EventSourcePolyfill(
      `${base}/api/dispatch-monitor/live-dispatch-sse?customerId=${customerId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    es.onmessage = (e: MessageEvent) => {
      try {
        const outer = JSON.parse(e.data as string) as {
          channel: string;
          message: string | Record<string, any>;
        };
        if (outer.channel !== channel) return;
        void dispatchApi.getLatestBlend().then((res) => {
          if (res?.dumpingPoints) {
            setGroups(buildGroups(res.dumpingPoints, blendMapRef.current));
          }
        });
        void fetchHourlyBlend();
      } catch (_) {}
    };
    es.onerror = (err) => {
      console.error('[BlendMonitoring SSE]', err);
    };
    return () => {
      es.close();
    };
  }, [accessToken, customerId, customerCode, fetchHourlyBlend]);

  const borderColor = palette.cardBorderColor;
  const headerBg = palette.toolBarBackground;
  const boxBg = palette.boxBackground;
  const summaryRowBg = `${palette.primary}14`;
  const altRowBg = `${palette.primary}08`;
  const textPrimary = 'text.primary';
  const textSecondary = 'text.secondary';
  const accentGreen = palette.success;
  const accentAmber = palette.warning;

  // Column widths (px) — sized to fit header text without truncation
  const W = {
    dp: 140,
    tg: 130,
    lp: 135,
    mat: 145,
    metric: 165,
    hour: 130,
    total: 110,
  };
  // Cumulative sticky-left offsets
  const L = {
    dp: 0,
    tg: 140,
    lp: 270,
    mat: 405,
    metric: 550,
  };

  // Sticky left/right helpers — sticky cells must have opaque bgcolor
  const sl = (left: number, bg: string = boxBg) => ({
    position: 'sticky' as const,
    left,
    zIndex: 2,
    bgcolor: bg,
  });
  const slH = (left: number) => ({ ...sl(left, headerBg), zIndex: 4 });
  const sr = (bg: string = boxBg) => ({
    position: 'sticky' as const,
    right: 0,
    zIndex: 2,
    bgcolor: bg,
    borderLeft: `2px solid ${borderColor}`,
  });
  const srH = () => ({ ...sr(headerBg), zIndex: 4 });

  const tableMinWidth =
    L.metric + W.metric + hourColumns.length * W.hour + W.total;

  const cellSx = {
    border: `1px solid ${borderColor}`,
    fontSize: 13,
    color: textPrimary,
    py: 0.75,
    px: 1.25,
    whiteSpace: 'nowrap' as const,
  };

  const headerCellSx = {
    ...cellSx,
    bgcolor: headerBg,
    fontWeight: 700,
    fontSize: 12,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: textSecondary,
  };

  const numCellSx = {
    ...cellSx,
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums',
  };

  return (
    <CustomMuiThemeProvider>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Forecast header cards */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {[
            {
              label: t('bm_forecast_grade'),
              value: forecastGrade,
              dot: accentGreen,
            },
            {
              label: t('bm_forecast_tonnage'),
              value: forecastTonnage,
              dot: accentAmber,
            },
          ].map(({ label, value, dot }) => (
            <Paper
              key={label}
              elevation={0}
              sx={{
                px: 2.5,
                py: 1.5,
                border: `1px solid ${borderColor}`,
                borderRadius: 2,
                bgcolor: palette.boxBackground,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: dot,
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    fontWeight: 600,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: textPrimary,
                    lineHeight: 1.2,
                  }}
                >
                  {value}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Table */}
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 300,
              bgcolor: palette.boxBackground,
              border: `1px solid ${borderColor}`,
              borderRadius: 2,
            }}
          >
            <SingleFadeLoader isFullScreen={false} message={t('cdp_loading')} />
          </Box>
        ) : groups.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
              bgcolor: palette.boxBackground,
              border: `1px solid ${borderColor}`,
              borderRadius: 2,
              color: textSecondary,
              fontSize: 14,
            }}
          >
            {t('bm_no_data')}
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${borderColor}`,
              borderRadius: 2,
              bgcolor: palette.boxBackground,
              overflow: 'visible',
            }}
          >
            <TableContainer
              ref={tableContainerRef}
              sx={{ overflowX: 'auto', borderRadius: 2 }}
            >
              <Table
                size="small"
                sx={{
                  borderCollapse: 'collapse',
                  minWidth: tableMinWidth,
                  width: '100%',
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...headerCellSx,
                        ...slH(L.dp),
                        minWidth: W.dp,
                        width: W.dp,
                      }}
                    >
                      {t('bm_dumping_point')}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...headerCellSx,
                        ...slH(L.tg),
                        minWidth: W.tg,
                        width: W.tg,
                      }}
                    >
                      {t('bm_target_grade')}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...headerCellSx,
                        ...slH(L.lp),
                        minWidth: W.lp,
                        width: W.lp,
                      }}
                    >
                      {t('bm_loading_point')}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...headerCellSx,
                        ...slH(L.mat),
                        minWidth: W.mat,
                        width: W.mat,
                      }}
                    >
                      {t('bm_material_grade')}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...headerCellSx,
                        ...slH(L.metric),
                        minWidth: W.metric,
                        width: W.metric,
                      }}
                    >
                      {t('bm_metric')}
                    </TableCell>
                    {hourColumns.map((col) => (
                      <TableCell
                        key={col.hour}
                        sx={{
                          ...headerCellSx,
                          textAlign: 'right',
                          minWidth: W.hour,
                          width: W.hour,
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        ...headerCellSx,
                        ...srH(),
                        textAlign: 'right',
                        color: textPrimary,
                        minWidth: W.total,
                        width: W.total,
                      }}
                    >
                      {t('bm_total')}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {groups.map((dp) => {
                    const dpKey = dp.name.toLowerCase();
                    const blendMinerals = Object.keys(dp.avgBlends);
                    const qualityRowCount = Math.max(1, blendMinerals.length);
                    const totalRowSpan =
                      1 + qualityRowCount + dp.loadingPoints.length * 2;
                    const dpAmberBg = enableDarkTheme
                      ? `${palette.warning}0F`
                      : `${palette.warning}0A`;

                    return (
                      <>
                        {/* Summary row 1: Tonnage */}
                        <TableRow
                          key={`${dp.name}-sum-ton`}
                          sx={{ bgcolor: summaryRowBg }}
                        >
                          <TableCell
                            rowSpan={totalRowSpan}
                            sx={{
                              ...cellSx,
                              ...sl(L.dp, dpAmberBg),
                              fontWeight: 700,
                              fontSize: 15,
                              verticalAlign: 'middle',
                              color: accentAmber,
                            }}
                          >
                            {dp.name}
                          </TableCell>
                          <TableCell
                            rowSpan={totalRowSpan}
                            sx={{
                              ...cellSx,
                              ...sl(L.tg),
                              fontWeight: 600,
                              verticalAlign: 'middle',
                              color: accentGreen,
                            }}
                          >
                            {dp.targetGrade}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...cellSx,
                              ...sl(L.lp),
                              color: textSecondary,
                            }}
                          />
                          <TableCell
                            sx={{
                              ...cellSx,
                              ...sl(L.mat),
                              color: textSecondary,
                            }}
                          />
                          <TableCell
                            sx={{ ...cellSx, ...sl(L.metric), fontWeight: 600 }}
                          >
                            {t('bm_tonnage')}
                          </TableCell>
                          {hourColumns.map((col) => {
                            const slot = hourlyMatrix[dpKey]?.[col.hour];
                            return (
                              <TableCell
                                key={col.hour}
                                sx={{ ...numCellSx, fontWeight: 600 }}
                              >
                                {slot
                                  ? slot.summaryTonnage.toLocaleString()
                                  : '—'}
                              </TableCell>
                            );
                          })}
                          <TableCell
                            sx={{ ...numCellSx, ...sr(), fontWeight: 700 }}
                          >
                            {dp.totalTonnage.toLocaleString()}
                          </TableCell>
                        </TableRow>

                        {/* Summary quality rows: one per mineral */}
                        {blendMinerals.length > 0 ? (
                          blendMinerals.map((mineral) => (
                            <TableRow
                              key={`${dp.name}-quality-${mineral}`}
                              sx={{ bgcolor: summaryRowBg }}
                            >
                              <TableCell
                                sx={{
                                  ...cellSx,
                                  ...sl(L.lp),
                                  color: textSecondary,
                                }}
                              />
                              <TableCell
                                sx={{
                                  ...cellSx,
                                  ...sl(L.mat),
                                  color: textSecondary,
                                }}
                              />
                              <TableCell
                                sx={{
                                  ...cellSx,
                                  ...sl(L.metric),
                                  fontWeight: 600,
                                  color: textSecondary,
                                }}
                              >
                                {`${t('bm_quality')} (% ${mineral})`}
                              </TableCell>
                              {hourColumns.map((col) => {
                                const slot = hourlyMatrix[dpKey]?.[col.hour];
                                const val = slot?.avgBlends[mineral];
                                return (
                                  <TableCell
                                    key={col.hour}
                                    sx={{ ...numCellSx, color: textSecondary }}
                                  >
                                    {val != null ? (val * 100).toFixed(2) : '—'}
                                  </TableCell>
                                );
                              })}
                              <TableCell
                                sx={{
                                  ...numCellSx,
                                  ...sr(),
                                  color: textSecondary,
                                  fontWeight: 600,
                                }}
                              >
                                {(dp.avgBlends[mineral] * 100).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow
                            key={`${dp.name}-quality-empty`}
                            sx={{ bgcolor: summaryRowBg }}
                          >
                            <TableCell
                              sx={{
                                ...cellSx,
                                ...sl(L.lp),
                                color: textSecondary,
                              }}
                            />
                            <TableCell
                              sx={{
                                ...cellSx,
                                ...sl(L.mat),
                                color: textSecondary,
                              }}
                            />
                            <TableCell
                              sx={{
                                ...cellSx,
                                ...sl(L.metric),
                                fontWeight: 600,
                                color: textSecondary,
                              }}
                            >
                              {t('bm_quality_pct')}
                            </TableCell>
                            {hourColumns.map((col) => (
                              <TableCell
                                key={col.hour}
                                sx={{ ...numCellSx, color: textSecondary }}
                              >
                                {'—'}
                              </TableCell>
                            ))}
                            <TableCell
                              sx={{
                                ...numCellSx,
                                ...sr(),
                                color: textSecondary,
                                fontWeight: 600,
                              }}
                            >
                              {'—'}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Per loading point rows */}
                        {dp.loadingPoints.map((lp, lpIdx) => {
                          const rowBg =
                            lpIdx % 2 === 1 ? altRowBg : 'transparent';
                          const stickyLpBg = lpIdx % 2 === 1 ? altRowBg : boxBg;
                          return (
                            <>
                              {/* Trips row */}
                              <TableRow
                                key={`${dp.name}-${lp.loadingPoint}-trips`}
                                sx={{ bgcolor: rowBg }}
                              >
                                <TableCell
                                  rowSpan={2}
                                  sx={{
                                    ...cellSx,
                                    ...sl(L.lp, stickyLpBg),
                                    fontWeight: 600,
                                    verticalAlign: 'middle',
                                    color: accentGreen,
                                  }}
                                >
                                  {lp.loadingPoint}
                                </TableCell>
                                <TableCell
                                  rowSpan={2}
                                  sx={{
                                    ...cellSx,
                                    ...sl(L.mat, stickyLpBg),
                                    verticalAlign: 'middle',
                                    color: textSecondary,
                                    fontSize: 12,
                                  }}
                                >
                                  {lp.materialGrade}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...cellSx,
                                    ...sl(L.metric, stickyLpBg),
                                  }}
                                >
                                  {t('bm_trips')}
                                </TableCell>
                                {hourColumns.map((col) => {
                                  const lpHourly =
                                    hourlyMatrix[dpKey]?.[col.hour]?.lpData[
                                      lp.loadingPoint
                                    ];
                                  return (
                                    <TableCell key={col.hour} sx={numCellSx}>
                                      {lpHourly != null ? lpHourly.trips : '—'}
                                    </TableCell>
                                  );
                                })}
                                <TableCell
                                  sx={{ ...numCellSx, ...sr(stickyLpBg) }}
                                >
                                  {lp.trips}
                                </TableCell>
                              </TableRow>

                              {/* Tonnage row */}
                              <TableRow
                                key={`${dp.name}-${lp.loadingPoint}-ton`}
                                sx={{ bgcolor: rowBg }}
                              >
                                <TableCell
                                  sx={{
                                    ...cellSx,
                                    ...sl(L.metric, stickyLpBg),
                                    color: textSecondary,
                                  }}
                                >
                                  {t('bm_tonnage')}
                                </TableCell>
                                {hourColumns.map((col) => {
                                  const lpHourly =
                                    hourlyMatrix[dpKey]?.[col.hour]?.lpData[
                                      lp.loadingPoint
                                    ];
                                  return (
                                    <TableCell
                                      key={col.hour}
                                      sx={{
                                        ...numCellSx,
                                        color: textSecondary,
                                      }}
                                    >
                                      {lpHourly != null
                                        ? lpHourly.tonnage.toLocaleString()
                                        : '—'}
                                    </TableCell>
                                  );
                                })}
                                <TableCell
                                  sx={{
                                    ...numCellSx,
                                    ...sr(stickyLpBg),
                                    color: textSecondary,
                                  }}
                                >
                                  {lp.tonnage.toLocaleString()}
                                </TableCell>
                              </TableRow>
                            </>
                          );
                        })}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </CustomMuiThemeProvider>
  );
};
